import { useState, useEffect, useCallback } from "react";
import { format } from "date-fns";
import { FiPlus, FiSearch } from "react-icons/fi";

import AppShell from "../components/AppShell";
import SearchBar from "../components/SearchBar";
import JournalEntryCard from "../components/JournalEntryCard";
import EntryEditor from "../components/EntryEditor";
import { useAuth } from "../context/AuthContext";
import { useWeekContext } from "../context/WeekContext";
import {
  subscribeToEntries,
  createEntry,
  patchEntry,
  removeEntry,
} from "../journal/journalService";
import type { JournalEntry, EntryPayload, NewEntry } from "../journal/types";

/** Strip HTML tags to produce a plain-text preview. */
function stripHtml(html: string): string {
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
}

const JournalPage = () => {
  const { user } = useAuth();
  const { unlockedWeek } = useWeekContext();

  const [entries, setEntries] = useState<JournalEntry[]>([]);
  
  // Logic: Only show spinner for the very first load
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // ── Real-time Firestore listener ────────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    
    const unsubscribe = subscribeToEntries(
      user.uid,
      (fetched) => { 
        setEntries(fetched); 
        setIsInitialLoading(false); 
      },
      (err) => { 
        console.error("Journal snapshot error:", err); 
        setIsInitialLoading(false); 
      },
    );
    return unsubscribe;
  }, [user]);

  // ── CRUD wrappers ──────────────────────────────────────────────────────
  const addEntry = useCallback(
    async (
      entry: Omit<NewEntry, "dateId" | "weekNumber" | "voiceNoteUrl"> & { voiceNoteUrl?: string },
      audioBlob?: Blob | null,
    ): Promise<boolean> => {
      if (!user) return false;
      try { 
        await createEntry(user.uid, entry, unlockedWeek, audioBlob); 
        return true; 
      }
      catch (err) { console.error("Error adding journal entry:", err); return false; }
    },
    [user, unlockedWeek],
  );

  const updateEntry = useCallback(
    async (id: string, entry: Partial<NewEntry>, audioBlob?: Blob | null): Promise<boolean> => {
      if (!user) return false;
      try { 
        await patchEntry(user.uid, id, entry, audioBlob); 
        return true; 
      }
      catch (err) { console.error("Error updating journal entry:", err); return false; }
    },
    [user],
  );

  const deleteEntry = useCallback(
    async (id: string) => {
      if (!user) return;
      const existing = entries.find((e) => e.id === id);
      try { await removeEntry(user.uid, id, existing?.voiceNoteUrl); }
      catch (err) { console.error("Error deleting journal entry:", err); }
    },
    [user, entries],
  );

  const [showEditor, setShowEditor] = useState(false);
  const [editingEntry, setEditingEntry] = useState<JournalEntry | null>(null);
  const [editorKey, setEditorKey] = useState(0);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // ── The "Snap" Save Logic ───────────────────────────────────────────────
  const handleSaveEntry = async (
    entry: EntryPayload,
    audioBlob?: Blob | null,
  ): Promise<boolean> => {
    const isEditing = !!editingEntry;
    const entryId = editingEntry?.id;

    closeEditor();

    if (isEditing && entryId) {
      return await updateEntry(entryId, entry, audioBlob);
    } else {
      return await addEntry(entry, audioBlob);
    }
  };

  const handleDeleteEntry = async (id: string) => {
    await deleteEntry(id);
    closeEditor();
  };

  const openEntry = (entry: JournalEntry) => {
    setEditingEntry(entry);
    setShowEditor(true);
  };

  const openNewEntry = () => {
    setEditingEntry(null);
    setEditorKey((k) => k + 1);
    setShowEditor(true);
  };

  const closeEditor = () => {
    setShowEditor(false);
    setEditingEntry(null);
  };

  const filteredEntries = entries.filter(
    (e) =>
      e.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      e.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getJournalSubtitle = (count: number) => {
    if (count === 0) return "A quiet space for your thoughts.";
    if (count === 1) return "First entry captured. You're doing something for yourself today.";
    if (count < 5) return `${count} moments saved. You're building a beautiful habit.`;
    return `${count} entries · Your journey is unfolding beautifully.`;
  };

  return (
    <AppShell
      headerConfig={{
        title: "Your Journal",
        label: "Journal",
        gradient: "linear-gradient(135deg, #7E5244, #C5A499)",
        height: 200,
        subtitle: getJournalSubtitle(entries.length),
        headerActions: (
          <>
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center text-white hover:bg-white/25 transition-colors border-0 cursor-pointer"
            >
              <FiSearch size={16} />
            </button>
          </>
        ),
        headerExtra: searchOpen ? (
          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search entries..."
            className="mt-4"
          />
        ) : undefined,
      }}
    >
      <div className="relative z-10 w-full min-h-screen bg-content-bg rounded-t-[32px] pt-6 pb-[72px]">
        {/* Quick prompt card */}
        <div className="px-6 full-rounded">
          <button
            onClick={openNewEntry}
            className="w-full bg-white rounded-2xl p-4 flex items-center gap-3 shadow-sm hover:shadow-md transition-all active:scale-[0.98] border-0 cursor-pointer"
          >
            <div className="w-10 h-10 rounded-full journal-gradient flex items-center justify-center">
              <FiPlus size={20} className="text-white" />
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold font-dm-sans text-brand-dark m-0">
                How was your day?
              </p>
              <p className="text-xs font-dm-sans text-gray-500 m-0 mt-0.5">
                Write or record a voice note
              </p>
            </div>
          </button>
        </div>

        {/* Entries list */}
        <div className="px-6 mt-6 pb-28">
          <h2 className="text-xs font-semibold font-dm-sans text-gray-400 mb-3 uppercase tracking-wide">
            Recent Entries
          </h2>

          {isInitialLoading ? (
            /* ── HIGH QUALITY BRANDED SPINNER ── */
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '80px 0',
              width: '100%'
            }}>
              <div style={{
                width: '50px',
                height: '50px',
                border: '4px solid rgba(126, 82, 68, 0.1)', // Using journal brown tint
                borderTop: '4px solid #7E5244',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
                marginBottom: '20px',
                boxSizing: 'border-box'
              }} />
              <div style={{ fontFamily: 'DM Sans, sans-serif', textAlign: 'center' }}>
                <p style={{
                  color: '#7E5244',
                  fontSize: '14px',
                  fontWeight: '600',
                  letterSpacing: '2px',
                  textTransform: 'uppercase',
                  margin: 0,
                  animation: 'pulse 1.5s ease-in-out infinite'
                }}>
                  BodyOwn
                </p>
              </div>
              <style>{`
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
              `}</style>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {filteredEntries.map((entry) => (
                <JournalEntryCard
                  key={entry.id}
                  title={entry.title}
                  preview={stripHtml(entry.content)}
                  date={format(entry.date, "MMM d, yyyy")}
                  dateId={entry.dateId}
                  weekNumber={entry.weekNumber}
                  hasVoiceNote={entry.hasVoiceNote}
                  onClick={() => openEntry(entry)}
                  onDelete={() => deleteEntry(entry.id)}
                />
              ))}
              {filteredEntries.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-gray-400 text-sm font-dm-sans">
                    {searchQuery ? "No entries found" : "No entries yet. Start writing!"}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Floating action button */}
      <button
        onClick={openNewEntry}
        className="fixed bottom-24 right-5 w-14 h-14 rounded-full journal-gradient flex items-center justify-center shadow-lg hover:shadow-xl transition-shadow active:scale-95 z-40 border-0 cursor-pointer"
      >
        <FiPlus size={24} className="text-white" />
      </button>

      <EntryEditor
        key={editingEntry?.id ?? `new-${editorKey}`}
        open={showEditor}
        onClose={closeEditor}
        onSave={handleSaveEntry}
        onDelete={editingEntry ? () => handleDeleteEntry(editingEntry.id) : undefined}
        initialTitle={editingEntry?.title ?? ""}
        initialContent={editingEntry?.content ?? ""}
        initialVoiceNoteUrl={editingEntry?.voiceNoteUrl ?? ""}
        initialVoiceTranscript={editingEntry?.voiceTranscript ?? ""}
        isEditing={!!editingEntry}
      />
    </AppShell>
  );
};

export default JournalPage;